// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Rafael Pereira Satyro

import {
  useLayoutEffect,
  useRef,
  useState,
  type ChangeEvent,
  type Ref,
} from 'react';
import { formatAmount, groupDigits, parseAmount } from './numberFormat';

/**
 * Props to spread onto a `<TextInput>` or a raw `<input>` so it renders a
 * numeric `value` grouped with thousands separators (3,000) while still
 * emitting a plain `number | undefined` upward.
 */
export interface GroupedAmountRegister {
  ref: Ref<HTMLInputElement>;
  value: string;
  inputMode: 'decimal';
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onFocus: () => void;
  onBlur: () => void;
}

/**
 * Controlled-input glue for monetary amount fields. The input shows the value
 * comma-grouped while the user types, the caret is kept in place across the
 * comma re-insertion (so editing feels native), and `onChange` receives the
 * parsed `number` (or `undefined` when blank/invalid). Switches the field off
 * the native `type="number"` control, which can neither display nor accept
 * commas, to a `type="text"` + `inputMode="decimal"` control.
 */
export function useGroupedAmount(
  value: unknown,
  onChange: (next: number | undefined) => void,
): GroupedAmountRegister {
  const ref = useRef<HTMLInputElement>(null);
  const focused = useRef(false);
  const pendingCaret = useRef<number | null>(null);
  const [display, setDisplay] = useState<string>(() => formatAmount(value));

  // Reflect external value changes (prefill, programmatic edits) into the
  // display — but never while the user is mid-edit, or an in-progress entry
  // like "3." would be reformatted out from under them.
  useLayoutEffect(() => {
    if (focused.current) return;
    const next = formatAmount(value);
    setDisplay((prev) => (prev === next ? prev : next));
  }, [value]);

  // After a grouped re-render the inserted/removed commas shift offsets, so put
  // the caret back where the user's content left it.
  useLayoutEffect(() => {
    if (pendingCaret.current != null && ref.current) {
      const pos = pendingCaret.current;
      ref.current.setSelectionRange(pos, pos);
    }
    pendingCaret.current = null;
  });

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    const caret = e.target.selectionStart ?? raw.length;
    // How many significant chars (digits / decimal point) sit left of the caret;
    // the caret is restored after the same count once commas are re-inserted.
    const significantLeft = (raw.slice(0, caret).match(/[\d.]/g) ?? []).length;
    const formatted = groupDigits(raw);

    let seen = 0;
    let nextCaret = formatted.length;
    for (let i = 0; i < formatted.length; i += 1) {
      if (seen >= significantLeft) {
        nextCaret = i;
        break;
      }
      if (/[\d.]/.test(formatted[i])) seen += 1;
    }
    pendingCaret.current = nextCaret;

    setDisplay(formatted);
    onChange(parseAmount(raw));
  };

  return {
    ref,
    value: display,
    inputMode: 'decimal',
    onChange: handleChange,
    onFocus: () => {
      focused.current = true;
    },
    onBlur: () => {
      focused.current = false;
      // Normalise on blur (drops a trailing "." and re-groups from the stored value).
      setDisplay(formatAmount(value));
    },
  };
}
